<template>
  <div class="rich-text-editor">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-group">
        <!-- Bold -->
        <button
          class="toolbar-btn"
          type="button"
          :class="{ active: activeFormats.bold }"
          title="Bold"
          @click="execCommand('bold')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.6 10.79C16.57 10.12 17.25 9.02 17.25 8C17.25 5.74 15.5 4 13.25 4H7V18H14.04C16.13 18 17.75 16.3 17.75 14.21C17.75 12.69 16.89 11.39 15.6 10.79ZM10 6.5H13C13.83 6.5 14.5 7.17 14.5 8C14.5 8.83 13.83 9.5 13 9.5H10V6.5ZM13.5 15.5H10V12.5H13.5C14.33 12.5 15 13.17 15 14C15 14.83 14.33 15.5 13.5 15.5Z" fill="currentColor"/>
          </svg>
        </button>
        <!-- Italic -->
        <button
          class="toolbar-btn"
          type="button"
          :class="{ active: activeFormats.italic }"
          title="Italic"
          @click="execCommand('italic')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 4V7H12.21L8.79 15H6V18H14V15H11.79L15.21 7H18V4H10Z" fill="currentColor"/>
          </svg>
        </button>
        <!-- Underline -->
        <button
          class="toolbar-btn"
          type="button"
          :class="{ active: activeFormats.underline }"
          title="Underline"
          @click="execCommand('underline')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 17C15.31 17 18 14.31 18 11V3H15.5V11C15.5 12.93 13.93 14.5 12 14.5C10.07 14.5 8.5 12.93 8.5 11V3H6V11C6 14.31 8.69 17 12 17ZM5 19V21H19V19H5Z" fill="currentColor"/>
          </svg>
        </button>
        <!-- Bullet List -->
        <button
          class="toolbar-btn"
          type="button"
          :class="{ active: activeFormats.bulletList }"
          title="Bullet List"
          @click="toggleList('ul')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 10.5C3.17 10.5 2.5 11.17 2.5 12C2.5 12.83 3.17 13.5 4 13.5C4.83 13.5 5.5 12.83 5.5 12C5.5 11.17 4.83 10.5 4 10.5ZM4 4.5C3.17 4.5 2.5 5.17 2.5 6C2.5 6.83 3.17 7.5 4 7.5C4.83 7.5 5.5 6.83 5.5 6C5.5 5.17 4.83 4.5 4 4.5ZM4 16.5C3.17 16.5 2.5 17.18 2.5 18C2.5 18.82 3.18 19.5 4 19.5C4.82 19.5 5.5 18.82 5.5 18C5.5 17.18 4.83 16.5 4 16.5ZM7 19H21V17H7V19ZM7 13H21V11H7V13ZM7 5V7H21V5H7Z" fill="currentColor"/>
          </svg>
        </button>
        <!-- Numbered List -->
        <button
          class="toolbar-btn"
          type="button"
          :class="{ active: activeFormats.numberedList }"
          title="Numbered List"
          @click="toggleList('ol')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 17H4V17.5H3V18.5H4V19H2V20H5V16H2V17ZM3 8H4V4H2V5H3V8ZM2 11H3.8L2 13.1V14H5V13H3.2L5 10.9V10H2V11ZM7 5V7H21V5H7ZM7 19H21V17H7V19ZM7 13H21V11H7V13Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Editor Content -->
    <div class="editor-wrapper">
      <div
        ref="editor"
        class="editor-content"
        :class="{ 'has-placeholder': !hasContent }"
        :data-placeholder="placeholder"
        contenteditable="true"
        @input="handleInput"
        @keydown.enter="handleEnter"
        @focus="handleFocus"
        @blur="handleBlur"
        @click="updateSelection"
        @keyup="updateSelection"
      ></div>
      
      <!-- Save Button -->
      <div class="editor-footer">
        <TextButton
          @click.stop="handleSave"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M6.75012 12.1275L3.62262 9L2.55762 10.0575L6.75012 14.25L15.7501 5.25L14.6926 4.1925L6.75012 12.1275Z" fill="#1F1F1F"/>
            </svg>
          </template>
          Save
        </TextButton>
      </div>
    </div>
  </div>
</template>

<script>
import TextButton from '@/views/commonComponents/TextButton.vue'
export default {
  name: 'RichTextEditor',
  components: {
    TextButton
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Type your content here...'
    },
    minHeight: {
      type: String,
      default: '150px'
    },
    maxLength: {
      type: Number,
      default: null
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showClearButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'clear', 'focus', 'blur', 'save'],
  data() {
    return {
      isFocused: false,
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
        bulletList: false,
        numberedList: false
      }
    };
  },
  computed: {
    hasContent() {
      if (!this.$refs.editor) return false;
      const text = this.$refs.editor.innerText?.trim() || '';
      return text.length > 0;
    },
    charCount() {
      if (!this.$refs.editor) return 0;
      return this.$refs.editor.innerText?.length || 0;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (this.$refs.editor && !this.isFocused) {
          if (this.$refs.editor.innerHTML !== newVal) {
            this.$refs.editor.innerHTML = newVal || '';
          }
        }
      }
    }
  },
  mounted() {
    if (this.$refs.editor) {
      this.$refs.editor.innerHTML = this.modelValue || '';
    }
  },
  methods: {
    // ─── Get current list context ───
    getCurrentListContext() {
      const sel = window.getSelection();
      if (!sel.rangeCount) return { inList: false, listType: null, listElement: null };
      
      let node = sel.anchorNode;
      
      // Walk up the DOM tree to find if we're in a list
      while (node && node !== this.$refs.editor) {
        if (node.nodeName === 'UL') {
          return { inList: true, listType: 'ul', listElement: node };
        }
        if (node.nodeName === 'OL') {
          return { inList: true, listType: 'ol', listElement: node };
        }
        node = node.parentNode;
      }
      
      return { inList: false, listType: null, listElement: null };
    },

    // ─── Toggle list ───
    toggleList(listType) {
      const editor = this.$refs.editor;
      if (!editor) return;

      const context = this.getCurrentListContext();
      
      // If we're in a list
      if (context.inList) {
        if (context.listType === listType) {
          // Same list type - toggle off (remove list)
          document.execCommand(listType === 'ul' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
        } else {
          // Different list type - convert
          // First remove current list
          document.execCommand(context.listType === 'ul' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
          // Then apply new list
          document.execCommand(listType === 'ul' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
        }
      } else {
        // Not in a list - create one
        document.execCommand(listType === 'ul' ? 'insertUnorderedList' : 'insertOrderedList', false, null);
      }

      this.updateActiveStates();
      this.emitUpdate();
      editor.focus();
    },

    // ─── Core commands ───
    execCommand(command, value = null) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      document.execCommand(command, false, value);
      this.$refs.editor?.focus();
      if (range && selection.rangeCount === 0) selection.addRange(range);
      this.updateActiveStates();
      this.emitUpdate();
    },

    // ─── Enter key ───
    handleEnter(event) {
      // Let browser handle list Enter behavior naturally
      // Browser automatically creates new <li> elements in lists
      return true;
    },

    // ─── State ───
    isCommandActive(command) {
      try { return document.queryCommandState(command); }
      catch (e) { return false; }
    },
    updateActiveStates() {
      this.activeFormats.bold = this.isCommandActive('bold');
      this.activeFormats.italic = this.isCommandActive('italic');
      this.activeFormats.underline = this.isCommandActive('underline');

      // Detect list state from DOM
      const context = this.getCurrentListContext();
      this.activeFormats.bulletList = context.listType === 'ul';
      this.activeFormats.numberedList = context.listType === 'ol';
      
      this.$forceUpdate();
    },
    emitUpdate() {
      if (this.$refs.editor) {
        this.$emit('update:modelValue', this.$refs.editor.innerHTML);
      }
    },
    handleInput(event) {
      this.emitUpdate();
      this.updateActiveStates();
    },
    handleFocus() {
      this.isFocused = true;
      this.updateActiveStates();
      this.$emit('focus');
    },
    handleBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },
    updateSelection() {
      this.updateActiveStates();
    },
    clearContent() {
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = '';
        this.$emit('update:modelValue', '');
        this.$emit('clear');
      }
    },
    focus() { this.$refs.editor?.focus(); },
    blur() { this.$refs.editor?.blur(); },
    getContent() { return this.$refs.editor?.innerHTML || ''; },
    getTextContent() { return this.$refs.editor?.innerText || ''; },
    handleSave() { this.$emit('save'); }
  }
};
</script>

<!-- no scoped to allow global styles for editor -->
<!-- otherwise, the list styles won't apply -->
<style scoped>
.rich-text-editor {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 5px;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #F2F2F2;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  color: #333;
  transition: all 0.15s ease;
}

.toolbar-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.toolbar-btn:active {
  background: rgba(0, 0, 0, 0.1);
}

.toolbar-btn.active {
  background: rgba(30, 70, 112, 0.12);
  color: #1E4670;
}

.toolbar-btn svg {
  width: 24px;
  height: 24px;
}

/* Editor Wrapper */
.editor-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #FFFFFF;
  padding: 12px 16px;
  gap: 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

/* Editor Content */
.editor-content {
  flex: 1;
  min-height: v-bind(minHeight);
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #333;
  outline: none;
  overflow-y: auto;
  word-wrap: break-word;
}

.editor-content:empty:before {
  content: attr(data-placeholder);
  color: #999;
  pointer-events: none;
}

.editor-content.has-placeholder:empty:before {
  content: attr(data-placeholder);
  color: #999;
  pointer-events: none;
}

/* Footer */
.editor-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
}
</style>

<style>
/* Editor Content Styles */
.editor-content p {
  margin: 0;
}

.editor-content ul,
.editor-content ol {
  margin: 0;
  padding-left: 0;
  list-style: none;
  counter-reset: item;
}

.editor-content li {
  margin: 0;
  padding-left: 21px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  position: relative;
}

/* Bullet list styling */
.editor-content ul > li::before {
  content: "• ";
  position: absolute;
  left: 6px;
}

/* Numbered list styling */
.editor-content ol {
  counter-reset: item;
}

.editor-content ol > li {
  counter-increment: item;
}

.editor-content ol > li::before {
  content: counter(item) ". ";
  position: absolute;
  left: 0;
}

.editor-content strong,
.editor-content b {
  font-weight: 700;
}

.editor-content em,
.editor-content i {
  font-style: italic;
  font-weight: 400;
}

.editor-content u {
  text-decoration: underline;
}
</style>
